/**
 * Deliverability dashboard — email health from Resend webhooks.
 *
 * Shows sent/delivered/bounced/complained/opened totals for the last N days,
 * key rates with Gmail alarm thresholds (>0.3% complaint, >5% bounce), a
 * timeline chart, and a table of recent bounces with reasons.
 */
import { useCallback, useEffect, useState } from "react";
import { teacherApi } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { AlertTriangle, RefreshCw, TrendingUp, TrendingDown, MailX, Bell } from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { d: 7, label: "7 dni" },
  { d: 30, label: "30 dni" },
  { d: 90, label: "90 dni" },
];

function StatCard({ label, value, sub, color = "bg-white" }) {
  return (
    <div className={`brut ${color} rounded-md p-4`} data-testid={`stat-${label}`}>
      <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#64748B]">{label}</div>
      <div className="text-3xl font-black mt-1">{value}</div>
      {sub && <div className="text-xs text-[#64748B] mt-1">{sub}</div>}
    </div>
  );
}

function RateCard({ label, value, alarm, ok }) {
  const color = alarm ? "bg-[#FEE2E2]" : (value > 0 ? "bg-[#FEF08A]" : "bg-[#D1FAE5]");
  const icon = alarm ? TrendingUp : (value > 0 ? TrendingUp : TrendingDown);
  const Icon = icon;
  return (
    <div className={`brut ${color} rounded-md p-4`} data-testid={`rate-${label}`}>
      <div className="text-xs tracking-[0.15em] uppercase font-bold flex items-center gap-1">
        <Icon className="w-3 h-3" strokeWidth={2.5} /> {label}
      </div>
      <div className="text-3xl font-black mt-1">{value}%</div>
      <div className="text-xs mt-1">{ok}</div>
    </div>
  );
}

/**
 * 24h early-warning widget — osobne od głównego wyboru zakresu.
 * Pokazuje bounce rate / complaint rate w ostatnich 24h z progami,
 * żeby admin od razu wiedział czy właśnie wysypały się jakieś wysyłki.
 * Kolor karty zmienia się na: zielony (ok), żółty (warning), czerwony (critical).
 */
function Widget24h({ snap }) {
  const bounceRate = Number(snap?.rates?.bounce_rate ?? 0);
  const complaintRate = Number(snap?.rates?.complaint_rate ?? 0);
  const warnings = snap?.warnings || [];
  const level = warnings.some((w) => w.level === "critical")
    ? "critical"
    : warnings.some((w) => w.level === "warning")
    ? "warning"
    : "ok";

  const palette = {
    ok:       { bg: "#D1FAE5", chip: "#065F46", label: "Zdrowo ✓", icon: TrendingDown },
    warning:  { bg: "#FEF08A", chip: "#78350F", label: "Ostrzeżenie",  icon: AlertTriangle },
    critical: { bg: "#FEE2E2", chip: "#991B1B", label: "Krytyczne",   icon: AlertTriangle },
  }[level];
  const Icon = palette.icon;

  // Thresholds — hardcoded zgodnie z backend `deliverability.py`, więc UI
  // nigdy nie zasugeruje admin'owi że jest OK gdy backend uzna breach.
  const BOUNCE_WARN = 2.0, BOUNCE_CRIT = 5.0;
  const COMPLAINT_CRIT = 0.3;

  const totals = snap?.aggregate || {};

  return (
    <div
      className="brut border-4 border-[#0F172A] rounded-md p-4 mb-6"
      style={{ backgroundColor: palette.bg }}
      data-testid="deliverability-24h"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" strokeWidth={2.5} />
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase font-black">
              Stan ostatnich 24h
            </div>
            <div className="font-black text-lg">
              {palette.label}
            </div>
          </div>
        </div>
        <div
          className="brut bg-white px-3 py-1 rounded font-black text-xs uppercase tracking-wide"
          style={{ color: palette.chip }}
        >
          {level === "ok" ? "Wszystko OK" : (level === "warning" ? "Monitoruj" : "Reaguj")}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Mini label="Wysłano"     value={totals.sent ?? 0}      />
        <Mini label="Doręczono"   value={totals.delivered ?? 0} />
        <Mini
          label="Bounce rate"
          value={`${bounceRate.toFixed(2)}%`}
          emphasize={bounceRate > BOUNCE_WARN}
          critical={bounceRate > BOUNCE_CRIT}
          hint={`próg: >${BOUNCE_WARN}% ostrzeżenie, >${BOUNCE_CRIT}% krytyczne`}
          testid="widget-24h-bounce"
        />
        <Mini
          label="Complaint rate"
          value={`${complaintRate.toFixed(2)}%`}
          critical={complaintRate > COMPLAINT_CRIT}
          hint={`próg: >${COMPLAINT_CRIT}% krytyczne (Gmail = spam folder)`}
          testid="widget-24h-complaint"
        />
      </div>

      {warnings.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="widget-24h-warnings">
          {warnings.map((w) => (
            <div
              key={w.code}
              className={`brut rounded px-3 py-2 text-xs ${
                w.level === "critical" ? "bg-[#FEE2E2]" : "bg-[#FEF3C7]"
              }`}
            >
              <b className="uppercase tracking-wider mr-1">
                {w.level === "critical" ? "⛔" : "⚠️"}
              </b>
              {w.message}
            </div>
          ))}
        </div>
      )}

      {level === "ok" && (totals.sent ?? 0) > 0 && (
        <div className="mt-3 text-xs text-[#065F46]">
          Alert zostanie wysłany adminom automatycznie gdy bounce przekroczy{" "}
          <b>{BOUNCE_CRIT}%</b> lub complaint przekroczy <b>{COMPLAINT_CRIT}%</b>{" "}
          w 24h (cron co 6h, dedup 6h per próg).
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, emphasize, critical, hint, testid }) {
  const bg = critical ? "#FCA5A5" : emphasize ? "#FDE68A" : "#FFFFFF";
  return (
    <div
      className="brut rounded px-3 py-2"
      style={{ backgroundColor: bg }}
      data-testid={testid}
      title={hint}
    >
      <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#334155]">
        {label}
      </div>
      <div className="font-black text-lg leading-tight">{value}</div>
    </div>
  );
}

export default function DeliverabilityTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snap24, setSnap24] = useState(null);  // 24h early-warning widget

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: body } = await teacherApi.get(`/admin/deliverability?days=${days}`);
      setData(body);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd wczytywania deliverability");
    } finally { setLoading(false); }
  }, [days]);

  // 24h widget — pobierane niezależnie od zakresu wyboru u góry (nawet jak
  // admin wybierze 30 dni, i tak chcemy wiedzieć co się dzieje w ostatnich 24h).
  const load24 = useCallback(async () => {
    try {
      const { data } = await teacherApi.get("/admin/deliverability?days=1");
      setSnap24(data);
    } catch {/* silent — główny widok już zgłosi błąd */}
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { load24(); }, [load24]);

  if (loading && !data) {
    return <div className="text-sm text-[#64748B]">Ładuję…</div>;
  }
  if (!data) return null;

  return (
    <div data-testid="deliverability-tab">
      {/* Header + range picker */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-display font-black text-2xl">Deliverability</h2>
        <div className="flex gap-2 ml-auto flex-wrap">
          {RANGES.map((r) => (
            <button
              key={r.d}
              onClick={() => setDays(r.d)}
              className={`brut brut-hover rounded-md px-3 py-1.5 text-xs font-bold ${
                days === r.d ? "bg-[#0F172A] text-white" : "bg-white"
              }`}
              data-testid={`range-${r.d}`}
            >
              {r.label}
            </button>
          ))}
          <button
            onClick={load}
            className="brut brut-hover bg-white rounded-md px-3 py-1.5 text-xs font-bold"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 inline mr-1 ${loading ? "animate-spin" : ""}`} strokeWidth={2.5} />
            Odśwież
          </button>
          <TestAlertButton />
        </div>
      </div>

      {/* ---- 24h early-warning widget ---- */}
      {snap24 && (
        <Widget24h snap={snap24} data-testid="deliverability-24h" />
      )}

      {/* Warnings */}
      {data.warnings.length > 0 && (
        <div className="space-y-2 mb-6">
          {data.warnings.map((w, i) => (
            <div
              key={i}
              className={`brut rounded-md p-4 flex items-start gap-3 ${
                w.level === "critical" ? "bg-[#FEE2E2] text-[#7F1D1D]" : "bg-[#FEF3C7] text-[#78350F]"
              }`}
              data-testid={`warning-${i}`}
            >
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
              <div className="text-sm font-bold">{w.message}</div>
            </div>
          ))}
        </div>
      )}

      {/* Totals grid */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label="Wysłano" value={data.aggregate.sent} sub={`za ${data.days} dni`} />
        <StatCard label="Dostarczono" value={data.aggregate.delivered} color="bg-[#D1FAE5]" />
        <StatCard label="Otwarte" value={data.aggregate.opened} color="bg-[#DBEAFE]" />
        <StatCard label="Odbite" value={data.aggregate.bounced}
                  color={data.aggregate.bounced > 0 ? "bg-[#FEE2E2]" : "bg-white"} />
        <StatCard label="Skargi" value={data.aggregate.complained}
                  color={data.aggregate.complained > 0 ? "bg-[#FEE2E2]" : "bg-white"} />
      </div>

      {/* Rates */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <RateCard
          label="Bounce rate"
          value={data.rates.bounce_rate}
          alarm={data.rates.bounce_rate > 5}
          ok="Cel: <2%. Alarm Gmail: >5%"
        />
        <RateCard
          label="Complaint rate"
          value={data.rates.complaint_rate}
          alarm={data.rates.complaint_rate > 0.3}
          ok="Cel: <0.1%. Alarm Gmail: >0.3%"
        />
        <RateCard
          label="Open rate"
          value={data.rates.open_rate}
          alarm={false}
          ok="Wymaga tracking pixel w mailach"
        />
      </div>

      {/* Timeline chart */}
      {data.timeline.length > 0 && (
        <div className="brut bg-white rounded-md p-4 mb-6" data-testid="timeline-chart">
          <div className="font-bold mb-3 text-sm tracking-[0.15em] uppercase">Timeline</div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={data.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ border: "2px solid #0F172A", borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="delivered" stroke="#059669" strokeWidth={2} name="Dostarczono" />
                <Line type="monotone" dataKey="opened" stroke="#2563EB" strokeWidth={2} name="Otwarte" />
                <Line type="monotone" dataKey="bounced" stroke="#DC2626" strokeWidth={2} name="Odbite" />
                <Line type="monotone" dataKey="complained" stroke="#7C2D12" strokeWidth={2} name="Skargi" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recent bounces */}
      <div className="brut bg-white rounded-md p-4" data-testid="recent-bounces">
        <div className="font-bold mb-3 text-sm tracking-[0.15em] uppercase flex items-center gap-2">
          <MailX className="w-4 h-4" strokeWidth={2.5} /> Ostatnie odbicia i skargi
        </div>
        {data.recent_bounces.length === 0 ? (
          <div className="text-sm text-[#64748B] py-4">
            Brak odbić w ostatnich {data.days} dniach 🎉
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#0F172A] text-left text-xs uppercase tracking-wider">
                  <th className="pb-2 pr-3">Data</th>
                  <th className="pb-2 pr-3">Adres</th>
                  <th className="pb-2 pr-3">Temat</th>
                  <th className="pb-2 pr-3">Typ</th>
                  <th className="pb-2">Powód</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_bounces.map((b, i) => (
                  <tr key={i} className="border-b border-[#E2E8F0]" data-testid={`bounce-row-${i}`}>
                    <td className="py-2 pr-3 text-xs text-[#64748B] whitespace-nowrap">
                      {b.created_at?.slice(0, 16).replace("T", " ")}
                    </td>
                    <td className="py-2 pr-3 text-xs font-bold">{b.to}</td>
                    <td className="py-2 pr-3 text-xs truncate max-w-xs">{b.subject}</td>
                    <td className="py-2 pr-3">
                      <span className={`brut text-xs font-bold px-2 py-0.5 rounded ${
                        b.status === "complained" ? "bg-[#7F1D1D] text-white" : "bg-[#FEE2E2] text-[#7F1D1D]"
                      }`}>
                        {b.status}
                      </span>
                    </td>
                    <td className="py-2 text-xs text-[#64748B]">{b.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function TestAlertButton() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await teacherApi.post("/admin/deliverability/test-alert");
      setResult(data);
      if (data.webhook_configured) {
        toast.success("Test wysłany — sprawdź Slack/Discord + skrzynkę admina");
      } else {
        toast.warning("Webhook nie skonfigurowany — wysłano tylko e-mail do adminów");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nie udało się wysłać testu");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={test}
        disabled={busy}
        className="brut brut-hover bg-[#FEF08A] rounded-md px-3 py-1.5 text-xs font-bold disabled:opacity-50"
        data-testid="test-alert-btn"
        title="Wyśle testowy alert do Slack/Discord + e-mail adminów"
      >
        <Bell className={`w-3 h-3 inline mr-1 ${busy ? "animate-pulse" : ""}`} strokeWidth={2.5} />
        {busy ? "Wysyłam…" : "Testuj alert"}
      </button>
      {result && (
        <span
          className={`brut text-xs font-bold px-2 py-0.5 rounded ${
            result.webhook_configured ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEF3C7] text-[#78350F]"
          }`}
          data-testid="test-alert-result"
        >
          {result.webhook_configured ? "webhook OK" : "webhook —"}
        </span>
      )}
    </div>
  );
}
